import type { AgentEvent } from '@/services/Conversations';

/**
 * Matches tool calls to their agent events by `toolCallId`.
 *
 * This used to be positional: the n-th `kb_search` event belonged to the n-th
 * `search_kb` call, and when the counts disagreed we returned nothing at all
 * rather than an off-by-one match. That was the honest thing to do with no join
 * key, but it meant the evidence panel vanished exactly when it was needed —
 * events are written fire-and-forget, so one dropped event shifted every
 * number after it.
 *
 * `back#SPEC-010` puts `tool_call_id` on the event, so the join is now exact.
 * Two consequences worth stating:
 *
 * - It works for **every** tool, not just `search_kb`. The old `TOOL_EVENT_TYPE`
 *   map had a single entry because only that one event was 1:1 with a call.
 * - A dropped event now costs one panel instead of all of them.
 *
 * Events without a `toolCallId` (the RAG supervisor's internals, scheduled
 * reports) are not tool results and simply do not appear here — `groupBySpan`
 * is what places those.
 */
export const correlateEvents = (events: AgentEvent[]): Record<string, AgentEvent> =>
	events.reduce<Record<string, AgentEvent>>(
		(acc, event) => (event.toolCallId ? { ...acc, [event.toolCallId]: event } : acc),
		{},
	);

/**
 * Groups events by the span they hang from, so the RAG supervisor's fan-out
 * renders as a tree instead of a flat list.
 *
 * A turn on the supervisor arm emits `kb_search` → `rag_classification` → N×
 * `kb_domain_search`. Those domains run concurrently on the backend
 * (`asyncio.gather`), so ordering by timestamp cannot tell you which child
 * belongs to which parent — and with two searches in one turn, neither can
 * anything else. `parentSpanId` can.
 *
 * Keyed by parent span id; events with no parent are roots and are not
 * included.
 */
export const groupBySpan = (events: AgentEvent[]): Record<string, AgentEvent[]> =>
	events.reduce<Record<string, AgentEvent[]>>((acc, event) => {
		if (!event.parentSpanId) return acc;
		return { ...acc, [event.parentSpanId]: [...(acc[event.parentSpanId] ?? []), event] };
	}, {});

/**
 * Whether this thread carries trace data at all.
 *
 * False for every conversation recorded before migration 0013 shipped. The UI
 * uses this to say "this conversation predates tracing" instead of showing an
 * empty evidence section, which would read as a bug rather than as history.
 */
export const hasTrace = (events: AgentEvent[]): boolean => events.some((event) => !!event.traceId);
